import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useDebouncedValue } from "@mantine/hooks";
import { Spotlight } from "@mantine/spotlight";
import { Group, Loader, Text } from "@mantine/core";
import {
  IconLayoutDashboard,
  IconSearch,
  IconSettings,
  IconUserCircle,
  IconUser,
  IconUsers,
} from "@tabler/icons-react";
import { hasAtLeast, isPortalFeatureEnabled, rolePrivilege } from "@wwrp/shared";
import type { Member } from "@wwrp/shared";
import { useAuth } from "../contexts/AuthContext.js";
import { getAccessRole } from "../api/auth.js";
import { membersApi } from "../api/members.js";
import { ROLE_LABEL } from "./RoleBadge.js";
import { getPortalFeatureKey, NAV_PAGES } from "../nav.js";
import { usePortalFeatures } from "../hooks/usePortalFeatures.js";

interface NavItem {
  id: string;
  label: string;
  description: string;
  keywords: string[];
  icon: React.ReactNode;
  path: string;
}

/** Returns true if the member's community status is considered active. */
function isActiveMember(m: Member): boolean {
  const s = m.communityStatus?.toLowerCase() ?? "";
  return s === "active" || s === "on_loa";
}

/** Sort spotlight member results: active first, then by role privilege desc, then name asc. */
function sortSpotlightMembers(items: Member[]): Member[] {
  return [...items].sort((a, b) => {
    const aActive = isActiveMember(a) ? 0 : 1;
    const bActive = isActiveMember(b) ? 0 : 1;
    if (aActive !== bActive) return aActive - bActive;
    const roleDiff = rolePrivilege(b.role) - rolePrivilege(a.role);
    if (roleDiff !== 0) return roleDiff;
    return (a.firstNameLastInitial ?? "").localeCompare(b.firstNameLastInitial ?? "");
  });
}

export function AppSpotlight() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const portalFeatures = usePortalFeatures();
  const role = getAccessRole(user);
  const [query, setQuery] = useState("");
  const [debouncedQuery] = useDebouncedValue(query, 250);

  const isStaff =
    (role ? hasAtLeast(role, "staff_in_training") : false) &&
    isPortalFeatureEnabled(portalFeatures.data?.features, "members");

  // Build nav items with the same access rules as the sidebar.
  const navItems = useMemo<NavItem[]>(() => {
    const items: NavItem[] = [
      {
        id: "dashboard",
        label: "Dashboard",
        description: "Announcements, community stats, and who is online",
        keywords: ["home", "announcements", "overview", "stats", "online"],
        icon: <IconLayoutDashboard size={18} stroke={1.5} />,
        path: "/",
      },
      {
        id: "my-profile",
        label: "My Profile",
        description: "Your profile, records, and LOA requests",
        keywords: ["me", "profile", "loa", "conduct", "records"],
        icon: <IconUserCircle size={18} stroke={1.5} />,
        path: "/my-profile",
      },
      {
        id: "settings",
        label: "Settings",
        description: "Manage your account and notification preferences",
        keywords: ["preferences", "notifications", "discord", "account"],
        icon: <IconSettings size={18} stroke={1.5} />,
        path: "/settings",
      },
    ];

    if (role && hasAtLeast(role, "staff_in_training")) {
      items.push({
        id: "members",
        label: "Staff Lookup",
        description: "Search and view staff profiles",
        keywords: ["search", "list", "staff", "profiles"],
        icon: <IconUsers size={18} stroke={1.5} />,
        path: "/members",
      });
    }

    for (const page of NAV_PAGES.filter(
      (page) =>
        page.category === "admin" || ["my-requests", "application-review"].includes(page.id),
    )) {
      const hasPageAccess = page.sysadminOnly
        ? Boolean(user?.isSysadmin && !user.viewingAsRole)
        : page.permissionKey
          ? Boolean(user?.permissions?.[page.permissionKey])
          : Boolean(
              !page.minRole ||
              (role !== null && role !== undefined && hasAtLeast(role, page.minRole)),
            );
      if (!hasPageAccess) continue;
      items.push({
        id: page.id,
        label: page.label,
        description: page.description,
        keywords: page.keywords,
        icon: <page.icon size={18} stroke={1.5} />,
        path: page.to,
      });
    }
    for (const page of NAV_PAGES.filter(
      (page) => page.category === "staff" && page.permissionKey === "da.view",
    )) {
      if (user?.permissions?.["da.view"])
        items.push({
          id: page.id,
          label: page.label,
          description: page.description,
          keywords: page.keywords,
          icon: <page.icon size={18} stroke={1.5} />,
          path: page.to,
        });
    }
    return items.filter((item) => {
      const page = NAV_PAGES.find((navPage) => navPage.id === item.id);
      const key = page ? getPortalFeatureKey(page) : null;
      return !key || isPortalFeatureEnabled(portalFeatures.data?.features, key);
    });
  }, [role, user, portalFeatures.data?.features]);

  // Client-side filter for nav items
  const filteredNav = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return navItems;
    return navItems.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q) ||
        item.keywords.some((k) => k.includes(q)),
    );
  }, [navItems, query]);

  const trimmed = debouncedQuery.trim();
  const canSearchMembers = isStaff && trimmed.length >= 2;

  const { data: memberResults, isFetching } = useQuery({
    queryKey: ["spotlight-members", trimmed],
    queryFn: () => membersApi.list({ q: trimmed, limit: 10 }),
    enabled: canSearchMembers,
    staleTime: 15_000,
  });

  const members = useMemo(
    () => sortSpotlightMembers(memberResults?.items ?? []).slice(0, 8),
    [memberResults],
  );
  const showMemberGroup = canSearchMembers && members.length > 0;
  const isEmpty = filteredNav.length === 0 && members.length === 0 && !isFetching;

  return (
    <Spotlight.Root
      query={query}
      onQueryChange={setQuery}
      shortcut={["mod+K", "mod+P", "mod+L"]}
      clearQueryOnClose
    >
      <Spotlight.Search
        placeholder="Search pages or members..."
        leftSection={<IconSearch size={18} stroke={1.5} />}
        rightSection={isFetching && canSearchMembers ? <Loader size="xs" /> : null}
        rightSectionPointerEvents="none"
      />

      <Spotlight.ActionsList>
        {isEmpty && <Spotlight.Empty>Nothing found...</Spotlight.Empty>}

        {filteredNav.length > 0 &&
          (showMemberGroup ? (
            <Spotlight.ActionsGroup label="Pages">
              {filteredNav.map((item) => (
                <Spotlight.Action
                  key={item.id}
                  label={item.label}
                  description={item.description}
                  leftSection={item.icon}
                  highlightQuery
                  onClick={() => navigate(item.path)}
                />
              ))}
            </Spotlight.ActionsGroup>
          ) : (
            filteredNav.map((item) => (
              <Spotlight.Action
                key={item.id}
                label={item.label}
                description={item.description}
                leftSection={item.icon}
                highlightQuery
                onClick={() => navigate(item.path)}
              />
            ))
          ))}

        {showMemberGroup && (
          <Spotlight.ActionsGroup label="Members">
            {members.map((m) => {
              const name = m.firstNameLastInitial ?? `Member #${m.id}`;
              // Prefer guild nickname as the displayed alias; fall back to discord username.
              const alias = m.guildNickname ?? m.discordUsername ?? null;
              const meta = [ROLE_LABEL[m.role], m.websiteId ?? null, m.primaryDepartment ?? null]
                .filter(Boolean)
                .join(" · ");
              return (
                <Spotlight.Action
                  key={`member-${m.id}`}
                  onClick={() => navigate(`/members/${m.id}`)}
                >
                  <Group wrap="nowrap" w="100%" gap="sm">
                    <IconUser
                      size={18}
                      stroke={1.5}
                      style={{ color: "var(--muted)", flexShrink: 0 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Group gap={6} wrap="nowrap" align="baseline">
                        <Text fw={500} truncate size="sm">
                          {name}
                        </Text>
                        {alias && (
                          <Text size="xs" c="dimmed" style={{ flexShrink: 0, fontWeight: 400 }}>
                            ({alias})
                          </Text>
                        )}
                      </Group>
                      <Text c="dimmed" size="xs" truncate>
                        {meta}
                      </Text>
                    </div>
                  </Group>
                </Spotlight.Action>
              );
            })}
          </Spotlight.ActionsGroup>
        )}
      </Spotlight.ActionsList>
    </Spotlight.Root>
  );
}
